import React from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogSubmit,
  AlertDialogTitle,
} from '@/components/common/AlertDialog'

import { useValueWhileOpen } from '@/hooks/common/useValueWhileOpen'

import type { UUID } from '@/types/Id'

export interface StoredFileRef {
  id: UUID
  name: string
  folder_id: UUID | null
  /** Version at match time: replacing with identical bytes records none, so this tells whether it did. */
  current_version_id?: UUID | null
}

export interface SameNameMatch<T extends { name: string }> {
  file: T
  stored: StoredFileRef
}

export interface SameNameSplit<T extends { name: string }> {
  matches: SameNameMatch<T>[]
  /** Local files with no namesake in the folder. */
  fresh: T[]
}

const nameKey = (name: string): string => name.trim().toLowerCase()

/**
 * Pairs each file about to be uploaded with the stored file of the same name in
 * the target folder, when there is one. Names match without regard to case.
 */
export const splitBySameName = <T extends { name: string }>(
  files: T[],
  stored: StoredFileRef[],
  folderId: UUID | null,
): SameNameSplit<T> => {
  const inFolder = new Map<string, StoredFileRef>()
  for (const file of stored) {
    const key = nameKey(file.name)
    if (file.folder_id === folderId && !inFolder.has(key)) {
      inFolder.set(key, file)
    }
  }
  const matches: SameNameMatch<T>[] = []
  const fresh: T[] = []
  for (const file of files) {
    const namesake = inFolder.get(nameKey(file.name))
    if (namesake) {
      matches.push({ file, stored: namesake })
    } else {
      fresh.push(file)
    }
  }
  return { matches, fresh }
}

interface SameNameFilesDialogProps {
  open: boolean
  /** The names that already exist in the folder. */
  names: string[]
  isPending?: boolean
  onReplace: () => void
  onKeepBoth: () => void
  onCancel: () => void
}

export const SameNameFilesDialog: React.FC<SameNameFilesDialogProps> = ({
  open,
  names,
  isPending = false,
  onReplace,
  onKeepBoth,
  onCancel,
}) => {
  // Answering the dialog clears the names, so it keeps saying the ones it was asked with on its way out.
  const asked = useValueWhileOpen(open, names)
  const one = asked.length === 1

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      onCancel()
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {one
              ? 'A file with this name already exists'
              : 'Files with these names already exist'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {one
              ? 'Replace it as a new version, or keep both?'
              : 'Replace them as new versions, or keep both copies of each?'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="max-h-40 overflow-y-auto rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-800 dark:border-primary-700 dark:bg-primary-800/40 dark:text-primary-200">
          {asked.map((name) => (
            <li key={name} className="truncate">
              {name}
            </li>
          ))}
        </ul>
        <AlertDialogFooter>
          <AlertDialogCancel variant="outline-compact" disabled={isPending}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            variant="outline-compact"
            onClick={onKeepBoth}
            disabled={isPending}
          >
            Keep both
          </AlertDialogAction>
          <AlertDialogSubmit
            type="button"
            variant="primary-compact"
            isLoading={isPending}
            disabled={isPending}
            onClick={onReplace}
          >
            {one ? 'Replace as new version' : 'Replace as new versions'}
          </AlertDialogSubmit>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default SameNameFilesDialog
